import { clstx } from '@payaca/utilities/cvaMerge';
import { ComponentPropsWithRef, ElementType } from 'react';

export type TextTag = 'p' | 'span' | 'div';
export type TextLevel = 'body' | 'supporting';
export type TextColour = 'primary' | 'muted' | 'positive' | 'negative';

const LEVEL_UTILITY: Record<TextLevel, string> = {
  body: 'ambrosia-body',
  supporting: 'ambrosia-supporting',
};

const COLOUR_UTILITY: Record<TextColour, string> = {
  primary: 'text-inherit',
  muted: 'text-gray-500',
  positive: 'text-green-600',
  negative: 'text-red-600',
};

/**
 * Native text attributes the component forwards (`aria-*`, `data-*`, `id`,
 * event handlers, etc.), minus the styling escape hatches `className` and
 * `style`. `ref` is included — React 19 passes it as a normal prop, so no
 * `forwardRef` is needed. Typed against `p`; when rendering `span`/`div` the
 * ref still resolves to the underlying element at runtime.
 */
type NativeTextProps = Omit<ComponentPropsWithRef<'p'>, 'className' | 'style'>;

export type TextProps = NativeTextProps & {
  /** Element to render. Defaults to `p`. Use `span` for inline text. */
  as?: TextTag;
  /** Text style. `body` (default) or `supporting` (smaller, secondary copy). */
  level?: TextLevel;
  /**
   * What the colour means. Defaults to the level's own colour: the surrounding
   * text colour for `body`, `muted` for `supporting`.
   */
  colour?: TextColour;
  /** Keep to one line, cutting overflow with an ellipsis. */
  truncate?: boolean;
};

/**
 * Body text with locked-in typography. Renders a `p` (default), `span` or `div`
 * styled from the shared design-system type scale.
 *
 * ## When to use
 *
 * - Any non-heading text: paragraphs, captions, inline copy.
 * - `level="supporting"` for smaller secondary copy (hints, metadata).
 * - `colour` when the colour carries meaning: `muted` for de-emphasised copy at
 *   either size, `positive` / `negative` for good and bad values or states,
 *   `primary` to give supporting text the surrounding text colour.
 *
 * ## When to choose another component
 *
 * - **Headings** — use `Heading`.
 * - **A form field label** — use the label built into `Field` / `Fieldset`,
 *   which wires up `htmlFor`.
 *
 * ## Composition rules
 *
 * `as` sets the HTML tag; `level` sets the look. Use `as="span"` for inline
 * text inside another block. `truncate` keeps the text to one line; its
 * container must be able to shrink (e.g. `min-w-0` in a flex row). Never reach for raw `<p>` / `<span>` or text
 * utility classes — this component is the only sanctioned way to render body
 * text, and it exposes no `className`, so its typography cannot be overridden.
 *
 * ✅ Correct:
 *
 * ```tsx
 * <Text>Standard paragraph copy.</Text>
 * <Text level="supporting">Last updated 2 hours ago</Text>
 * <Text as="span">inline</Text>
 * <Text colour="negative">Payment failed</Text>
 * ```
 *
 * ❌ Wrong:
 *
 * ```tsx
 * <p className="text-sm text-gray-500">Last updated 2 hours ago</p>
 * ```
 */
const Text = ({
  as = 'p',
  level = 'body',
  colour,
  truncate,
  ...rest
}: TextProps) => {
  const Tag = as as ElementType;

  return (
    <Tag
      {...rest}
      className={clstx(
        LEVEL_UTILITY[level],
        colour && COLOUR_UTILITY[colour],
        truncate && 'truncate'
      )}
    />
  );
};

export default Text;
